import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { X, Check, Container, Calendar, Palette, Clock } from 'lucide-react';

// Predefined color palette
const COLOR_PALETTE = [
  // Row 1 - Primary colors
  '#ef4444', '#f97316', '#eab308', '#22c55e', '#14b8a6', '#3b82f6', '#8b5cf6', '#ec4899',
  // Row 2 - Lighter variants
  '#fca5a5', '#fdba74', '#fde047', '#86efac', '#5eead4', '#93c5fd', '#c4b5fd', '#f9a8d4',
  // Row 3 - Neutrals
  '#ffffff', '#d1d5db', '#9ca3af', '#6b7280', '#4b5563', '#374151', '#1f2937', '#000000',
];

// Generate time options in 12-hour format with 30-minute increments
const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour < 12 ? 'AM' : 'PM';
      const displayTime = `${hour12}:${minute.toString().padStart(2, '0')} ${ampm}`;
      const value24 = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
      options.push({ display: displayTime, value: value24 });
    }
  }
  return options;
};

const TIME_OPTIONS = generateTimeOptions();

// Convert 24-hour time to 12-hour display format
const to12HourFormat = (time24) => {
  if (!time24) return '';
  const [hours, minutes] = time24.split(':').map(Number);
  const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours < 12 ? 'AM' : 'PM';
  return `${hour12}:${minutes.toString().padStart(2, '0')} ${ampm}`;
};

export const CellDateColorPicker = ({ 
  currentColor, 
  currentDate,
  currentTimeFrom,
  currentTimeTo,
  onColorChange, 
  onDateChange,
  onTimeChange,
  children,
  disabled = false,
  containerNumber = null,
  fieldLabel = 'Date',
  showTimePicker = false
}) => {
  const [open, setOpen] = useState(false);
  const [dateValue, setDateValue] = useState('');
  const [timeFromValue, setTimeFromValue] = useState('');
  const [timeToValue, setTimeToValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Format date for input (YYYY-MM-DD)
  useEffect(() => {
    if (currentDate) {
      const date = new Date(currentDate);
      if (!isNaN(date.getTime())) {
        setDateValue(date.toISOString().split('T')[0]);
      }
    } else {
      setDateValue('');
    }
    setTimeFromValue(currentTimeFrom || '');
    setTimeToValue(currentTimeTo || '');
  }, [currentDate, currentTimeFrom, currentTimeTo, open]);

  const handleColorSelect = (color) => {
    onColorChange(color);
  };

  const handleClearColor = () => {
    onColorChange(null);
  };

  // Auto-save when date changes
  const handleDateChange = async (newValue) => {
    setDateValue(newValue);
    if (newValue) {
      setSaving(true);
      try {
        await onDateChange(newValue);
        if (!showTimePicker) {
          setOpen(false);
        }
      } catch (e) {
        console.error('Failed to save date:', e);
      } finally {
        setSaving(false);
      }
    }
  };

  // Handle time from change with auto-set time to
  const handleTimeFromChange = async (time) => {
    setTimeFromValue(time);
    if (!onTimeChange) return;

    // Cleared `From` → also clear `To` and persist the cleared range
    // so the saved record matches what the user sees.
    if (!time) {
      setTimeToValue('');
      setSaving(true);
      try {
        await onTimeChange('', '');
      } catch (e) {
        console.error('Failed to save time:', e);
      } finally {
        setSaving(false);
      }
      return;
    }

    // Auto-set time_to to 1 hour after time_from
    const [hours, minutes] = time.split(':').map(Number);
    const toHours = (hours + 1) % 24;
    const toTime = `${String(toHours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    setTimeToValue(toTime);

    setSaving(true);
    try {
      await onTimeChange(time, toTime);
    } catch (e) {
      console.error('Failed to save time:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleTimeToChange = async (time) => {
    setTimeToValue(time);
    if (onTimeChange) {
      setSaving(true);
      try {
        await onTimeChange(timeFromValue, time);
      } catch (e) {
        console.error('Failed to save time:', e);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSaveDate = async () => {
    setSaving(true);
    try {
      await onDateChange(dateValue || null);
      if (showTimePicker && onTimeChange) {
        await onTimeChange(timeFromValue || null, timeToValue || null);
      }
      setOpen(false);
    } catch (e) {
      console.error('Failed to save date:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleClearDate = async () => {
    setSaving(true);
    try {
      setDateValue('');
      setTimeFromValue('');
      setTimeToValue('');
      await onDateChange(null);
      if (showTimePicker && onTimeChange) {
        await onTimeChange(null, null);
      }
      setOpen(false);
    } catch (e) {
      console.error('Failed to clear date:', e);
    } finally {
      setSaving(false);
    }
  };

  const handleClick = (e) => {
    if (disabled) return;
    e.preventDefault();
    e.stopPropagation();
    setOpen(true);
  };

  // Long press handler for mobile
  const handleTouchStart = (e) => {
    if (disabled) return;
    const timer = setTimeout(() => {
      setOpen(true);
    }, 400);
    e.target.dataset.longPressTimer = timer;
  };

  const handleTouchEnd = (e) => {
    if (e.target.dataset.longPressTimer) {
      clearTimeout(parseInt(e.target.dataset.longPressTimer));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild disabled={disabled}>
        <div 
          onClick={handleClick}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          className="w-full h-full min-h-[28px] flex items-center cursor-pointer"
          data-testid="cell-date-picker-trigger"
        >
          {children}
        </div>
      </PopoverTrigger>
      <PopoverContent 
        className={`${showTimePicker ? 'w-[340px]' : 'w-[300px]'} p-4 bg-slate-900 border-slate-700`}
        align="start"
        sideOffset={5}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="space-y-4">
          {/* Container Number Header */}
          {containerNumber && (
            <div className="flex items-center gap-2 pb-3 border-b border-slate-700">
              <Container className="h-5 w-5 text-blue-400 flex-shrink-0" />
              <span className="text-base font-mono font-semibold text-blue-400 truncate">
                {containerNumber}
              </span>
            </div>
          )}
          
          {/* Date Picker Section */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-purple-400" />
              <Label className="text-sm font-medium text-white">{fieldLabel}</Label>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={dateValue}
                onChange={(e) => handleDateChange(e.target.value)}
                className="flex-1 h-10 bg-slate-800 border-slate-600 text-white"
              />
              <Button
                size="sm"
                onClick={handleSaveDate}
                disabled={saving}
                className="h-10 px-4 bg-green-600 hover:bg-green-500"
              >
                {saving ? (
                  <span className="animate-spin">↻</span>
                ) : (
                  <Check className="h-4 w-4" />
                )}
              </Button>
              {currentDate && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClearDate}
                  disabled={saving}
                  className="h-10 px-2 text-slate-400 hover:text-white"
                  title="Clear date"
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>
          
          {/* Time Picker Section - Only for appointments */}
          {showTimePicker && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-amber-400" />
                <Label className="text-sm font-medium text-white">Time Window</Label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">From</Label>
                  <Select 
                    value={timeFromValue || "none"} 
                    onValueChange={(v) => handleTimeFromChange(v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white h-9">
                      <SelectValue placeholder="Select time">
                        {timeFromValue ? to12HourFormat(timeFromValue) : "Select time"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                      <SelectItem value="none" className="text-slate-400">Select time</SelectItem>
                      {TIME_OPTIONS.map(option => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value} 
                          className="text-white"
                        >
                          {option.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">To</Label>
                  <Select 
                    value={timeToValue || "none"} 
                    onValueChange={(v) => handleTimeToChange(v === "none" ? "" : v)}
                  >
                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white h-9">
                      <SelectValue placeholder="Select time">
                        {timeToValue ? to12HourFormat(timeToValue) : "Select time"}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
                      <SelectItem value="none" className="text-slate-400">Select time</SelectItem>
                      {TIME_OPTIONS.map(option => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value} 
                          className="text-white"
                        >
                          {option.display}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}
          
          <Separator className="bg-slate-700" />
          
          {/* Color Picker Section */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Palette className="h-4 w-4 text-pink-400" />
                <Label className="text-sm font-medium text-white">Text Color</Label>
              </div>
              {currentColor && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearColor}
                  className="text-slate-400 hover:text-white h-7 px-2"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear
                </Button>
              )}
            </div>
            
            {/* Color Grid */}
            <div className="grid grid-cols-8 gap-1.5">
              {COLOR_PALETTE.map((color) => (
                <button
                  key={color}
                  onClick={() => handleColorSelect(color)}
                  className={`w-7 h-7 rounded-md border transition-transform hover:scale-110 active:scale-95 flex items-center justify-center touch-manipulation ${
                    currentColor === color 
                      ? 'border-white ring-2 ring-blue-500' 
                      : 'border-slate-600 hover:border-slate-400'
                  }`}
                  style={{ backgroundColor: color }}
                  title={color}
                >
                  {currentColor === color && (
                    <Check className={`h-3 w-3 ${color === '#ffffff' || color === '#fde047' || color === '#86efac' ? 'text-black' : 'text-white'}`} />
                  )}
                </button>
              ))}
            </div>

            {/* Custom color input */}
            <div className="flex items-center gap-2 pt-2">
              <input
                type="color"
                value={currentColor || '#3b82f6'}
                onChange={(e) => handleColorSelect(e.target.value)}
                className="w-8 h-8 rounded cursor-pointer border-0 bg-transparent touch-manipulation"
                title="Custom color"
              />
              <span className="text-xs text-slate-400">Custom color</span>
            </div>
          </div>
          
          <p className="text-[10px] text-slate-500 text-center pt-2 border-t border-slate-800">
            Click cell to edit • Colors save automatically
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default CellDateColorPicker;
